import React, { useState, ReactNode } from 'react';
import { TopNav } from './TopNav';
import { LeftSidebar } from './LeftSidebar';
import { RightAgentPane } from './RightAgentPane';
import { MobileBottomNav } from '../mobile/MobileBottomNav';
import { CameraCapture } from '../mobile/CameraCapture';
import { ProjectCreateForm } from '../marketplace/ProjectCreateForm';
import { PhotoAssignmentModal, PhotoAssignment } from '../shared/PhotoAssignmentModal';
import { ToastContainer, ToastProps } from '../shared/Toast';
import { useIsMobile } from '../../hooks/useIsMobile';
import { useAgentPane } from '../../context/AgentPaneContext';
import { useNavigate } from 'react-router-dom';
import { imagesApi, itemsApi, aiApi } from '../../utils/api';

interface LayoutShellProps {
  children: ReactNode;
}

export const LayoutShell: React.FC<LayoutShellProps> = ({ children }) => {
  const [leftSidebarOpen, setLeftSidebarOpen] = useState(false);
  const [showCamera, setShowCamera] = useState(false);
  const [showCreateProject, setShowCreateProject] = useState(false);
  const [showPhotoAssignment, setShowPhotoAssignment] = useState(false);
  const [pendingImageData, setPendingImageData] = useState<string | null>(null);
  const [toasts, setToasts] = useState<Array<{ id: string } & ToastProps>>([]);
  
  const navigate = useNavigate();
  const isMobile = useIsMobile();
  const { toggle: toggleAgentPane } = useAgentPane();

  // Toast management
  const addToast = (toast: Omit<ToastProps, 'onClose'>) => {
    const id = Date.now().toString();
    setToasts(prev => [...prev, { id, ...toast, onClose: () => removeToast(id) }]);
    return id;
  };

  const updateToast = (id: string, updates: Partial<ToastProps>) => {
    setToasts(prev => prev.map(toast => 
      toast.id === id ? { ...toast, ...updates } : toast
    ));
  };

  const removeToast = (id: string) => {
    setToasts(prev => prev.filter(toast => toast.id !== id));
  };

  const toggleLeftSidebar = () => setLeftSidebarOpen(!leftSidebarOpen);
  const closeLeftSidebar = () => setLeftSidebarOpen(false);

  const handleTakePhoto = () => {
    setShowPhotoAssignment(true);
  };

  const handlePhotoAssignment = (assignment: PhotoAssignment) => {
    setShowPhotoAssignment(false);
    
    if (assignment.processLater) {
      // User wants to just save photos without processing
      setShowCamera(true);
    } else {
      // Store assignment for later use and show camera
      setPendingImageData(JSON.stringify(assignment));
      setShowCamera(true);
    }
  };

  const handleOpenAI = () => {
    toggleAgentPane();
  };

  const handleNewProject = () => {
    setShowCreateProject(true);
  };

  const handleCameraCapture = async (imageData: string, voiceAnnotation?: string, croppedImages?: Array<{imageData: string, detection: any, voiceAnnotation?: string}>) => {
    try {
      const timestamp = Date.now();
      console.log(`📸 [${timestamp}] handleCameraCapture called`);
      console.log(`   - Voice annotation: "${voiceAnnotation}"`);
      console.log(`   - Cropped images: ${croppedImages?.length || 0}`);
      
      const filename = `camera_capture_${timestamp}.jpg`;
      
      // Prepare item photos array (if we have detected items)
      const itemPhotos = croppedImages && croppedImages.length > 0
        ? croppedImages.map((cropped, index) => {
            console.log(`   - Item ${index + 1}: ${cropped.detection.class}, voiceAnnotation: "${cropped.voiceAnnotation || 'none'}"`);
            return {
              imageData: cropped.imageData,
              detection: {
                class: cropped.detection.class,
                confidence: cropped.detection.confidence,
                boundingBox: cropped.detection.boundingBox
              },
              voiceAnnotation: cropped.voiceAnnotation || undefined // Use per-item voice annotation!
            };
          })
        : undefined;

      console.log(`📸 [${timestamp}] Saving 1 photo with ${itemPhotos?.length || 0} item photos embedded`);

      // Save the main photo with all item photos embedded
      const saveResponse = await imagesApi.saveWithItems({
        filename,
        imageData,
        voiceAnnotation,
        itemPhotos
      });

      if (!saveResponse.data.success) {
        throw new Error('Failed to save photo');
      }

      const photoId = saveResponse.data.data?.photoId;
      console.log(`✅ Photo saved (ID: ${photoId}) with ${itemPhotos?.length || 0} item photos`);

      // Notify other components that a new photo was added
      window.dispatchEvent(new CustomEvent('photo-added'));

      // Check if we should auto-create items
      let shouldCreateItems = true; // Default to true
      if (pendingImageData) {
        try {
          const assignment: PhotoAssignment = JSON.parse(pendingImageData);
          shouldCreateItems = assignment.createItems;
        } catch (error) {
          console.error('Failed to parse assignment data:', error);
        }
      }

      // Automatically create items from detected objects OR from the main photo if enabled
      if (shouldCreateItems && itemPhotos && itemPhotos.length > 0) {
        // Multiple items from detections
        console.log(`📦 Auto-creating ${itemPhotos.length} items from detections...`);
        
        // Show progress toast
        const toastId = addToast({
          message: `Creating items from detections...`,
          type: 'progress',
          duration: 0,
          progress: { current: 0, total: itemPhotos.length }
        });
        
        let successCount = 0;
        
        for (let i = 0; i < itemPhotos.length; i++) {
          const itemPhoto = itemPhotos[i];
          try {
            console.log(`  Creating item ${i + 1}/${itemPhotos.length}: ${itemPhoto.detection.class}`);
            
            // Update progress toast
            updateToast(toastId, {
              progress: { current: i, total: itemPhotos.length }
            });
            
            // Extract just the base64 part (remove data:image/jpeg;base64, prefix if present)
            let base64Data = itemPhoto.imageData;
            if (base64Data.startsWith('data:')) {
              base64Data = base64Data.split(',')[1];
            }
            
            // Get AI analysis for this detected object
            let aiEstimation: any = null;
            try {
              // Use item-specific voice annotation if available, otherwise fall back to general voice annotation
              // Don't mention the detection class to OpenAI as it may be incorrect - let OpenAI analyze the image itself
              const itemDescription = itemPhoto.voiceAnnotation || voiceAnnotation || undefined;
              
              const aiResponse = await aiApi.estimate({
                base64Image: base64Data,
                // Don't send detection class - it's often wrong and biases OpenAI's analysis
                description: itemDescription
              });
              
              if (aiResponse.data.success) {
                aiEstimation = aiResponse.data.data;
              }
            } catch (aiError) {
              console.warn(`  AI analysis failed for item ${i + 1}, using detection data only:`, aiError);
            }
            
            // Capitalize the detection class for better presentation
            const capitalizedClass = itemPhoto.detection.class
              .split(' ')
              .map((word: string) => word.charAt(0).toUpperCase() + word.slice(1))
              .join(' ');
            
            // Create item data
            const itemData: any = {
              title: aiEstimation?.recommendedTitle || capitalizedClass,
              description: aiEstimation?.notes || (itemPhoto.voiceAnnotation || `Object detected with ${Math.round(itemPhoto.detection.confidence * 100)}% confidence. Please add description.`),
              images: [itemPhoto.imageData], // Use full data URL for item image
              condition: 'used' as const,
              price: aiEstimation?.estimatedValue || 0,
              currency: 'NZD',
              materials: aiEstimation?.attributes || [],
              status: 'draft' as const,
              sourcePhoto: photoId || undefined // Link back to parent photo so it won't show as separate photo entry
            };
            
            // Include AI analysis if available
            if (aiEstimation) {
              itemData.aiAnalysis = {
                estimatedValue: aiEstimation.estimatedValue,
                confidence: aiEstimation.confidence,
                notes: aiEstimation.notes,
                recommendedTitle: aiEstimation.recommendedTitle,
                attributes: aiEstimation.attributes,
                reasoning: aiEstimation.reasoning
              };
            }
            
            // Create the item
            const itemResponse = await itemsApi.create(itemData);
            
            if (itemResponse.data.success) {
              console.log(`  ✅ Item ${i + 1} created: ${itemData.title}`);
              successCount++;
              
              // Update progress
              updateToast(toastId, {
                progress: { current: i + 1, total: itemPhotos.length }
              });
            } else {
              console.error(`  ❌ Failed to create item ${i + 1}`);
            }
            
          } catch (error) {
            console.error(`  ❌ Failed to create item ${i + 1}:`, error);
          }
        }
        
        console.log('✅ All items created automatically');
        
        // Remove progress toast and show completion toast
        removeToast(toastId);
        addToast({
          message: `Successfully created ${successCount} item${successCount !== 1 ? 's' : ''}`,
          type: 'success',
          duration: 4000
        });
      } else if (shouldCreateItems && (!itemPhotos || itemPhotos.length === 0)) {
        // Single item from main photo (when Multiple Items Per Photo is disabled)
        console.log(`📦 Auto-creating single item from main photo...`);
        
        try {
          // Show creating toast
          const toastId = addToast({
            message: 'Creating item from photo...',
            type: 'progress',
            duration: 0
          });
          
          // Extract just the base64 part
          let base64Data = imageData;
          if (base64Data.startsWith('data:')) {
            base64Data = base64Data.split(',')[1];
          }
          
          // Get AI analysis for the photo
          let aiEstimation: any = null;
          try {
            console.log('📊 Requesting AI analysis for main photo...');
            const aiResponse = await aiApi.estimate({
              base64Image: base64Data,
              description: voiceAnnotation || undefined
            });
            
            console.log('📊 AI response:', aiResponse.data);
            
            if (aiResponse.data.success) {
              aiEstimation = aiResponse.data.data;
              console.log('✅ AI estimation received:', {
                title: aiEstimation?.recommendedTitle,
                value: aiEstimation?.estimatedValue,
                confidence: aiEstimation?.confidence
              });
            } else {
              console.warn('⚠️ AI response not successful:', aiResponse.data);
            }
          } catch (aiError) {
            console.error('❌ AI analysis failed, creating item without AI data:', aiError);
          }
          
          // Create item data from main photo
          const itemData: any = {
            title: aiEstimation?.recommendedTitle || 'Untitled Item',
            description: aiEstimation?.notes || (voiceAnnotation || 'Item created from photo. Please add description.'),
            images: [imageData], // Use full data URL
            condition: 'used' as const,
            price: aiEstimation?.estimatedValue || 0,
            currency: 'NZD',
            materials: aiEstimation?.attributes || [],
            status: 'draft' as const,
            sourcePhoto: photoId || undefined
          };
          
          // Include AI analysis if available (same as multiple items path)
          if (aiEstimation) {
            itemData.aiAnalysis = {
              estimatedValue: aiEstimation.estimatedValue,
              confidence: aiEstimation.confidence,
              notes: aiEstimation.notes,
              recommendedTitle: aiEstimation.recommendedTitle,
              attributes: aiEstimation.attributes,
              reasoning: aiEstimation.reasoning,
              analysisDate: new Date().toISOString()
            };
          }
          
          const createResponse = await itemsApi.create(itemData);
          
          if (createResponse.data.success) {
            console.log('✅ Item created successfully from main photo');
            
            // Notify other components
            window.dispatchEvent(new CustomEvent('item-added'));
            
            // Remove progress toast and show success
            removeToast(toastId);
            addToast({
              message: 'Item created successfully',
              type: 'success',
              duration: 4000
            });
          } else {
            throw new Error('Failed to create item');
          }
        } catch (error) {
          console.error('❌ Failed to create item from main photo:', error);
          addToast({
            message: 'Failed to create item',
            type: 'error',
            duration: 4000
          });
        }
      } else if (!shouldCreateItems && itemPhotos && itemPhotos.length > 0) {
        console.log('✅ Photo saved (auto-create disabled)');
        addToast({
          message: `Photo saved with ${itemPhotos.length} detection${itemPhotos.length !== 1 ? 's' : ''}`,
          type: 'info',
          duration: 3000
        });
      } else {
        console.log('✅ Photo saved (no auto-create)');
        addToast({
          message: 'Photo saved successfully',
          type: 'success',
          duration: 3000
        });
      }
      
    } catch (error) {
      console.error('Failed to save camera photo:', error);
    }
  };

  const handleProjectCreated = (project: any) => {
    console.log('Project created:', project);
    setShowCreateProject(false);
    // Navigate to the projects page to show the new project
    navigate('/projects');
  };

  return (
    <div className="min-h-screen bg-slate-50 dark:bg-slate-900">
      {/* Top Navigation */}
      <TopNav
        onLeftSidebarToggle={toggleLeftSidebar}
        leftSidebarOpen={leftSidebarOpen}
        onTakePhoto={handleTakePhoto}
      />

      <div className="flex h-[calc(100vh-64px)]">
        {/* Left Sidebar */}
        <LeftSidebar
          isOpen={leftSidebarOpen}
          onClose={closeLeftSidebar}
        />

        {/* Main Content */}
        <main className={`flex-1 overflow-y-auto ${isMobile ? 'pb-20' : ''}`}>
          {children}
        </main>

        {/* Right Agent Pane */}
        <RightAgentPane />
      </div>

      {/* Mobile Bottom Navigation */}
      {isMobile && (
        <MobileBottomNav
          onTakePhoto={handleTakePhoto}
          onOpenAI={handleOpenAI}
        />
      )}

      {/* Camera Capture Modal */}
      <CameraCapture
        isOpen={showCamera}
        onClose={() => {
          setShowCamera(false);
          setPendingImageData(null); // Clear assignment when closing camera
        }}
        onCapture={handleCameraCapture}
      />

      {/* Photo Assignment Modal */}
      <PhotoAssignmentModal
        isOpen={showPhotoAssignment}
        onClose={() => setShowPhotoAssignment(false)}
        onConfirm={handlePhotoAssignment}
      />

      {/* Project Create Modal */}
      {showCreateProject && (
        <ProjectCreateForm
          onSuccess={handleProjectCreated}
          onCancel={() => setShowCreateProject(false)}
        />
      )}

      {/* Toast Notifications */}
      <ToastContainer toasts={toasts} onRemove={removeToast} />
    </div>
  );
};
